'use client'

import { usePathname } from 'next/navigation'
import Image from 'next/image'
import Shopmn from '@/assets/logo.svg'
import { formatImageURL, getFontClass, getTextColorStyles, getVendorPrimaryColor } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { getVendorPhones } from '@/utils/vendorPhones'
import { Mail, Phone, ShieldCheck, ShieldX } from 'lucide-react'
import { useNavigation } from '@/lib/commerce/hooks'

const socialIcons = {
  facebook: (props: React.SVGProps<SVGSVGElement>) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path
        fillRule="evenodd"
        d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
        clipRule="evenodd"
      />
    </svg>
  ),
  instagram: (props: React.SVGProps<SVGSVGElement>) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path
        fillRule="evenodd"
        d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  twitter: (props: React.SVGProps<SVGSVGElement>) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1742 10.5689L12.8709 11.5655L18.6861 19.8835H16.2995L11.5541 13.096V13.0956Z" />
    </svg>
  ),
  youtube: (props: React.SVGProps<SVGSVGElement>) => (
    <svg fill="currentColor" viewBox="0 0 24 24" {...props}>
      <path
        fillRule="evenodd"
        d="M19.812 5.418c.861.23 1.538.907 1.768 1.768C21.998 8.746 22 12 22 12s0 3.255-.418 4.814a2.504 2.504 0 0 1-1.768 1.768c-1.56.419-7.814.419-7.814.419s-6.255 0-7.814-.419a2.505 2.505 0 0 1-1.768-1.768C2 15.255 2 12 2 12s0-3.255.417-4.814a2.507 2.507 0 0 1 1.768-1.768C5.744 5 11.998 5 11.998 5s6.255 0 7.814.418ZM15.194 12 10 15V9l5.194 3Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

export default function Footer({ vendor, subscription }: { vendor?: any, subscription?: any }) {
  const pathname = usePathname()
  const fontClass = getFontClass(vendor?.font)
  const primaryColor = getVendorPrimaryColor(vendor)
  const phones = getVendorPhones(vendor)
  const { className: textColorClass, style: textColorStyle } = getTextColorStyles(primaryColor)
  const { footer } = useNavigation({ vendor, subscription })
  const { info, help, socials, hasInfoSection } = footer
  const socialLinks = socials.map(s => ({ ...s, icon: socialIcons[s.platform] }))

  return (
    <footer className={`${fontClass} border-t border-border bg-background mt-20`}>
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-8 pt-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start gap-y-7">
            <div className="flex flex-col items-center lg:items-start gap-3">
              {vendor?.logo ? (
                <img src={formatImageURL(vendor?.logo)} alt="logo" className='max-h-[36px] sm:max-h-[40px] max-w-[190px] w-auto object-cover' />
              ) : (
                <div className={`text-xl sm:text-2xl font-bold tracking-tight ${textColorClass} hover:opacity-80 transition-opacity`} style={textColorStyle}>
                  {vendor?.name}
                </div>
              )}
              <p className="text-[13px] leading-relaxed text-muted-foreground text-center lg:text-start max-w-sm">
                {vendor?.description || (
                  <>
                    Анхны хиймэл оюун ухаант цахим дэлгүүрээ нэг минутанд{' '}
                    <a href="https://shop.mn" target="_blank" rel="noopener noreferrer" className={`text-gradient hover:opacity-80 font-semibold transition-opacity`}>
                      Shop.MN
                    </a>
                    -ээр нээж, бизнесээ эхлүүлээрэй.
                  </>
                )}
              </p>
            </div>
            {vendor?.isDanVerified ? (
              <a href={navigateUrl('/status', pathname)} className="inline-flex items-stretch rounded-xl border border-border bg-background overflow-hidden hover:border-foreground/30 transition-colors">
                <div className="p-3 grid place-items-center text-white shrink-0 bg-emerald-600">
                  <ShieldCheck className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div className="px-3.5 py-2 flex flex-col justify-center">
                  <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">Баталгаажсан</span>
                  <div className="text-[11.5px] leading-tight mt-0.5 text-muted-foreground">
                    Улсын бүртгэлээр баталгаажсан
                  </div>
                </div>
              </a>
            ) : (
              <a href={navigateUrl('/status', pathname)} className="inline-flex items-stretch rounded-xl border border-border bg-background overflow-hidden hover:border-foreground/30 transition-colors">
                <div className="p-3 grid place-items-center text-white shrink-0 bg-red-600">
                  <ShieldX className="h-6 w-6" strokeWidth={2.5} />
                </div>
                <div className="px-3.5 py-2 flex flex-col justify-center">
                  <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">Баталгаажаагүй</span>
                  <div className="text-[11.5px] leading-tight mt-0.5 text-muted-foreground">
                    Улсын бүртгэлээр баталгаажаагүй
                  </div>
                </div>
              </a>
            )}
            {socialLinks.length > 0 && (
              <div className="w-full flex flex-col items-center lg:items-start">
                <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-muted-foreground mb-3">Бидэнтэй холбогдох</div>
                <div className="flex items-center gap-2">
                  {socialLinks.map((item) => (
                    <a key={item.name} href={item.href} target="_blank" rel="noopener noreferrer" aria-label={item.name} className="h-10 w-10 rounded-full border border-border text-muted-foreground hover:text-foreground hover:bg-muted grid place-items-center transition-colors">
                      <item.icon aria-hidden="true" className="h-4.5 w-4.5" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {hasInfoSection && (
              <div className='text-center sm:text-start'>
                <div className="text-[13px] font-semibold tracking-[-0.005em] text-foreground">Мэдээлэл</div>
                <ul role="list" className="mt-4 space-y-2.5">
                  {info.map((item: any) => (
                    <li key={item.name}>
                      <a href={navigateUrl(item.href, pathname)} className="text-[13.5px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className='text-center sm:text-start'>
              <div className="text-[13px] font-semibold tracking-[-0.005em] text-foreground">Үйлчилгээ</div>
              <ul role="list" className="mt-4 space-y-2.5">
                {help.map((item: any) => (
                  <li key={item.name}>
                    <a href={navigateUrl(item.href, pathname)} className="text-[13.5px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            {(phones.length > 0 || vendor?.email) && (
              <div className="col-span-2 sm:col-span-1 text-center sm:text-start">
                <div className="text-[13px] font-semibold tracking-[-0.005em] text-foreground">Холбоо барих</div>
                <ul role="list" className="mt-4 space-y-2.5">
                  {phones.length > 0 && (
                    <li className="inline-flex items-center justify-center sm:justify-start gap-2 text-[13.5px] text-muted-foreground">
                      <Phone className="h-4 w-4 shrink-0" />
                      <span>
                        {phones.map((p, i) => (
                          <span key={p}>{i > 0 && ', '}<a href={`tel:${p}`} className="hover:text-foreground cursor-pointer transition-colors">{p}</a></span>
                        ))}
                      </span>
                    </li>
                  )}
                  {vendor?.email && (
                    <li>
                      <a href={`mailto:${vendor.email}`} className="inline-flex items-center justify-center sm:justify-start gap-2 text-[13.5px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors max-w-full">
                        <Mail className="h-4 w-4 shrink-0" />
                        <span className='break-all'>{vendor.email}</span>
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-8 py-5 flex flex-col md:flex-row md:items-center justify-between gap-4 text-[12px] text-muted-foreground mb-24 lg:mb-0">
          <span className="text-center md:text-start">&copy; {new Date().getFullYear()} {vendor?.name || 'Shop.mn'}. Бүх эрх хуулиар хамгаалагдсан.</span>
          <a href="https://shop.mn" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center md:justify-start gap-1.5 hover:text-foreground transition-colors">
            <span>Онлайн худалдааны шийдэл</span>
            <Image src={Shopmn} alt="Shop.mn" className='h-4 w-auto' />
          </a>
        </div>
      </div>
    </footer>
  )
}